import React, { useState, FormEvent, useEffect, useRef } from 'react';
import './Register.scss';
import { BaseReact } from '../../base.model';
import LogoFullSvg from '../visuals/LogoSvg';
import Input from '../Forms/Input';
import Button from '../Forms/Button';
import { Link } from 'react-router-dom';

import Select from '../Forms/Select';
import { getCookies } from '../../services/document.cookie';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { REGIMEN_OPTIONS } from '../../utils/regimen.options';
import Turnstile, { TurnstileHandle, TURNSTILE_ENABLED } from '../Forms/Turnstile';


interface RegisterProps extends BaseReact { }

const REST_BASE_URL = (process.env.REACT_APP_HTTP_URL || '').replace(/\/graphql\/?$/, '');

function Register({ history, location }: RegisterProps) {

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordConfirm, setPasswordConfirm] = useState('')

    // Account data
    const [rfc, setRfc] = useState('');
    const [regimen, setRegimen] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [lugarExpedicion, setLugarExpedicion] = useState('');

    const [registerVersion, setRegisterVersion] = useState<'lite' | 'profesional'>('lite')

    const [registering, setRegistering] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef<TurnstileHandle>(null);

    const [hasError, setHasError] = useState<{ [key: string]: boolean }>({})


    useEffect(() => {
        const query = location.search.replace('?', '').split('&').filter(s => !!s).reduce((acc, curr) => {
            const [key, value] = curr.split('=');
            acc[key] = value;
            return acc;
        }, {} as { [key: string]: string });
        if (query['version']) {
            setRegisterVersion(query['version'] as 'lite' | 'profesional')
        }
        console.log('Query: ', query);
    }, [location])

    useEffect(() => {
        const { fp_prof_profileid } = getCookies();
        if (fp_prof_profileid) {
            history.replace('/dashboard');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const _setEmail = (e: string) => {
        setEmail(e.trim().toLowerCase())
    }

    const _setCompanyName = (e: string) => {
        setCompanyName(e.toUpperCase().trimLeft())
    }

    const _setRfc = (e: string) => {
        setRfc(e.toUpperCase().trimLeft().trimRight())
    }

    const submitRegistration = (e: FormEvent) => {
        e.preventDefault();
        if (passwordConfirm !== password) {
            return;
        }
        if (!password || !rfc || !companyName || !email || !lugarExpedicion || !regimen) {
            setHasError({
                rfc: !rfc,
                password: !password,
                companyName: !companyName,
                email: !email,
                lugarExpedicion: !lugarExpedicion,
                regimen: !regimen,
            })
            return
        }
        setHasError({})
        if (TURNSTILE_ENABLED && !turnstileToken) {
            sharedToasterSubject.next({ type: 'error', message: 'Espera a que termine la verificación de seguridad.' });
            return;
        }
        setRegistering(true);
        fetch(`${REST_BASE_URL}/registeraccount`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email,
                password,
                regimenFiscal: regimen,
                nombre: companyName,
                rfc,
                lugarExpedicion,
                registerVersion,
                adOrigin: null,
                turnstileToken,
            }),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({} as { redirect?: string; error?: string }));
                if (response.ok && data.redirect) {
                    // BE has set the signed cookie; jump to the dashboard URL it told us to.
                    window.location.replace(data.redirect);
                    return;
                }
                // BE encodes validation/conflict/upstream failures as 400/409/502 with { error }.
                sharedToasterSubject.next({
                    type: 'error',
                    message: data.error || 'No se pudo crear la cuenta.',
                });
                // Turnstile tokens are single use, request a fresh one before the next attempt
                turnstileRef.current?.reset();
                setRegistering(false);
            })
            .catch((err) => {
                console.error('Error Registration: ', err);
                sharedToasterSubject.next({
                    type: 'error',
                    message: 'Error de red, intenta de nuevo.',
                });
                turnstileRef.current?.reset();
                setRegistering(false);
            });
    }

    const regimenOptions = REGIMEN_OPTIONS;

    return (
        <div id="Register">
            <form className="_loginForm" onSubmit={submitRegistration}>
                <LogoFullSvg size={252} />
                <div className="rightSecction">
                    <div className="row two sm-two">
                        <div className="useTwo">
                            <Input hasError={hasError['email']} type="text" label="Email a registrar" value={email} onChange={_setEmail} placeholder="Tu email" />
                        </div>
                    </div>
                    <div className="row two xs-gap-compressed">
                        <Input type="password" label="Contraseña" value={password} onChange={setPassword} placeholder="Contraseña" hasError={(password && passwordConfirm && (password !== passwordConfirm)) || hasError['password']} />
                        <Input type="password" label="Confirmar Contraseña" value={passwordConfirm} onChange={setPasswordConfirm} placeholder="Repetir Contraseña" hasError={password && passwordConfirm && (password !== passwordConfirm)} />
                    </div>
                    <Input hasError={hasError['companyName']} type="text" label="Nombre de tu Emisor o Razón Social" value={companyName} onChange={_setCompanyName} placeholder="No incluír siglas (S.A, C.V, etc.)" />
                    <div className="row two xs-gap-compressed">
                        <Input hasError={hasError['rfc']} type="text" label="RFC" value={rfc} onChange={_setRfc} placeholder="Tu RFC" />
                        <Input hasError={hasError['lugarExpedicion'] || (lugarExpedicion && lugarExpedicion.length > 5)} type="text" label="Codigo postal Fiscal" value={lugarExpedicion} onChange={setLugarExpedicion} placeholder="76020" />
                    </div>
                    <Select hasError={hasError['regimen']} label="Régimen" value={regimen} onChange={setRegimen} placeholder="Escoge tu régimen" options={regimenOptions} />
                    <Turnstile ref={turnstileRef} action="register" onToken={setTurnstileToken} />

                    <div className="actions">
                        <span className="alternate">
                            ¿Ya tienes cuenta?
                            <Link to="/login">
                                Sign in
                            </Link>
                        </span>
                        <Button primary={true} type="submit" disabled={registering || (TURNSTILE_ENABLED && !turnstileToken)}>
                            {
                                registering ?
                                    <span className='small'>Registrando tu cuenta...</span>
                                    :
                                    <span>Registar</span>
                            }
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default Register;
